import { Coordinate, Trip } from "@fairfleet/geotab";
import { driverIdOf } from "@/hooks/users/useDrivers";
import { toCoordinate } from "@/utilities/toCoordinate";

/** What the trips of a vehicle can say about a moment in time. */
export interface TripContext {
  /**
   * The driver as an id. `Get Trip` returns `driver` as a bare reference carrying nothing but
   * its id, so the name has to be looked up separately — see `useDriverNames`.
   */
  driverId: string | null;
  /** The odometer at the end of the trip, in kilometres. */
  odometerKm: number | null;
  /** Where the trip ended. */
  location: Coordinate | null;
}

const NOTHING_KNOWN: TripContext = { driverId: null, odometerKm: null, location: null };

/**
 * Builds a lookup that answers what the vehicle was doing at a given moment.
 *
 * Charging and refuelling both happen while the vehicle is parked, which is to say between two
 * trips — so the trips already fetched for the consumption figures also carry the driver, the
 * odometer and the position for those events. Reading them from here rather than asking Geotab
 * for a status-data and log-record window around every single event keeps the report inside its
 * API call budget: a fleet-wide period has thousands of charge and fill-up events, and two extra
 * calls each would cost more than all the consumption data put together.
 *
 * @param trips - The vehicle trips, in any order.
 * @returns A lookup from a timestamp to what is known about that moment.
 */
export function createTripLookup(trips: Trip[]): (time: number) => TripContext {
  const sorted = [...trips].sort((a, b) => stopTime(a) - stopTime(b));

  return (time: number) => {
    if (sorted.length === 0) {
      return NOTHING_KNOWN;
    }

    const trip = tripAround(sorted, time);

    return {
      driverId: driverIdOf(trip.driver),
      // Trip.odometer is in metres, unlike Trip.distance which is in kilometres.
      odometerKm: Number.isFinite(trip.odometer) ? trip.odometer / 1000 : null,
      location: toCoordinate(trip.stopPoint),
    };
  };
}

/**
 * The trip that describes a moment: the one under way at the time, or failing that the one that
 * ended nearest to it, the event having happened in the stretch parked around it.
 *
 * By binary search rather than a scan, because this is called once per charge event and once per
 * fill-up, against a list that can hold a month of trips.
 *
 * @param sorted - The trips, ordered by stop time.
 * @param time - The moment in question.
 */
function tripAround(sorted: Trip[], time: number) {
  // First trip whose stop is at or after the moment. A vehicle is only ever on one trip at a
  // time, so if any trip was under way it is this one.
  const at = firstStoppingAtOrAfter(sorted, time);

  const candidate = sorted[at];
  if (candidate && new Date(candidate.start).getTime() <= time) {
    return candidate;
  }

  // Otherwise the moment falls between two trips, and the nearer of the pair around it wins.
  const before = sorted[at - 1];
  if (!candidate) return before;
  if (!before) return candidate;

  return time - stopTime(before) <= stopTime(candidate) - time ? before : candidate;
}

function firstStoppingAtOrAfter(sorted: Trip[], time: number) {
  let low = 0;
  let high = sorted.length;

  while (low < high) {
    const middle = (low + high) >>> 1;
    if (stopTime(sorted[middle]) < time) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }

  return low;
}

function stopTime(trip: Trip) {
  return new Date(trip.stop).getTime();
}
